import {
  applyTranslationsToContainer,
  getCurrentLanguage,
  loadCreateCommonTranslations,
  resolveCreateCommonText,
} from '../translations.js';

const getFieldEditPageKey = () => {
  const path = (typeof window !== 'undefined' && window.location?.pathname) || '';
  if (path.includes('create-scenario')) return 'create-scenario';
  if (path.includes('create-video')) return 'create-video';
  return 'create-character';
};

const getPlaceholderKey = () => {
  const page = getFieldEditPageKey();
  if (page === 'create-scenario') return 'field_edit.placeholder_scenario';
  if (page === 'create-video') return 'field_edit.placeholder_video';
  return 'field_edit.placeholder_recruit';
};

export const applyFieldEditModalTranslations = (modal) => {
  if (!modal) return;
  const textarea = modal.querySelector('#ai-field-edit-instruction');
  if (textarea) textarea.setAttribute('data-translate-placeholder', getPlaceholderKey());
  applyTranslationsToContainer(modal, 'createCommon', getCurrentLanguage());
};

export const ensureFieldEditModal = async () => {
  let modal = document.getElementById('ai-field-edit-modal');
  await loadCreateCommonTranslations(getCurrentLanguage());

  if (modal) {
    applyFieldEditModalTranslations(modal);
    return modal;
  }

  modal = document.createElement('div');
  modal.id = 'ai-field-edit-modal';
  modal.className = 'modal ai-field-edit-modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-hidden', 'true');

  modal.innerHTML = `
    <div class="modal-content ai-field-modal-content" role="document" aria-labelledby="ai-field-edit-title">
      <button type="button" class="close-btn" id="ai-field-edit-close" data-translate-attr="aria-label:field_edit.close" aria-label="Close">×</button>
      <div class="ai-field-modal-body">
        <h3 id="ai-field-edit-title" class="ai-field-modal-title" data-translate="field_edit.title">Edit with AI</h3>
        <div id="ai-field-edit-subtitle" class="ai-field-modal-subtitle"></div>
        <label class="form-field">
          <span class="form-label" data-translate="field_edit.instruction_label">What do you want to change?</span>
          <textarea id="ai-field-edit-instruction" class="form-textarea form-textarea--tall form-textarea--taller" data-translate-placeholder="field_edit.placeholder_recruit" placeholder="e.g. make it shorter, more specific, add details..."></textarea>
        </label>
        <div class="form-actions form-actions--right">
          <button type="button" class="btn btn-secondary" id="ai-field-edit-cancel" data-translate="field_edit.cancel">Cancel</button>
          <button type="button" class="btn btn-primary" id="ai-field-edit-apply">
            <i class="fas fa-magic" aria-hidden="true"></i> <span data-translate="field_edit.apply">Apply</span>
          </button>
        </div>
      </div>
    </div>
  `;

  modal.addEventListener('click', (e) => {
    if (e.target === modal) modal.dispatchEvent(new CustomEvent('aiFieldEdit:cancel'));
  });

  document.body.appendChild(modal);
  applyFieldEditModalTranslations(modal);
  return modal;
};

export const openFieldEditModal = async ({ fieldLabel }) => {
  const modal = await ensureFieldEditModal();
  const closeBtn = modal.querySelector('#ai-field-edit-close');
  const cancelBtn = modal.querySelector('#ai-field-edit-cancel');
  const applyBtn = modal.querySelector('#ai-field-edit-apply');
  const subtitle = modal.querySelector('#ai-field-edit-subtitle');
  const textarea = modal.querySelector('#ai-field-edit-instruction');

  applyFieldEditModalTranslations(modal);
  if (subtitle) subtitle.textContent = resolveCreateCommonText('field_edit.editing_prefix', { field: fieldLabel });
  if (textarea) textarea.value = '';

  modal.classList.add('show');
  modal.setAttribute('aria-hidden', 'false');
  document.body.classList.add('modal-open');

  const previousActive = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const textareaEl = textarea instanceof HTMLTextAreaElement ? textarea : null;
  if (textareaEl) setTimeout(() => textareaEl.focus(), 0);

  return await new Promise((resolve) => {
    let settled = false;

    const cleanup = () => {
      modal.removeEventListener('aiFieldEdit:cancel', handleCancel);
      document.removeEventListener('keydown', handleKeyDown, true);
      if (closeBtn) closeBtn.removeEventListener('click', handleCancel);
      if (cancelBtn) cancelBtn.removeEventListener('click', handleCancel);
      if (applyBtn) applyBtn.removeEventListener('click', handleApply);
    };

    const close = (value) => {
      if (settled) return;
      settled = true;
      cleanup();
      modal.classList.remove('show');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('modal-open');
      previousActive?.focus();
      resolve(value);
    };

    function handleCancel() { close(null); }
    function handleApply() {
      const val = textarea ? textarea.value.trim() : '';
      if (!val) { close(null); return; }
      close(val);
    }

    function handleKeyDown(e) {
      if (e.key === 'Escape') { e.preventDefault(); handleCancel(); return; }
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); handleApply(); }
    }

    modal.addEventListener('aiFieldEdit:cancel', handleCancel);
    document.addEventListener('keydown', handleKeyDown, true);
    if (closeBtn) closeBtn.addEventListener('click', handleCancel);
    if (cancelBtn) cancelBtn.addEventListener('click', handleCancel);
    if (applyBtn) applyBtn.addEventListener('click', handleApply);
  });
};

if (!window.__fieldEditModalLangListener) {
  window.__fieldEditModalLangListener = true;
  document.addEventListener('languageSwitched', () => {
    const modal = document.getElementById('ai-field-edit-modal');
    if (modal) {
      loadCreateCommonTranslations(getCurrentLanguage()).then(() => {
        applyFieldEditModalTranslations(modal);
      });
    }
  });
}
